import {
    DndContext,
    DragEndEvent,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowBendDownLeft, PencilSimple, X } from "@phosphor-icons/react";
import { TElement } from "@udecode/plate-common";
import { addMinutes, format } from "date-fns";
import { Languages } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import React, { ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { Tweet } from "react-tweet";
import { toast } from "sonner";
import { useMutation } from "urql";

import { ControlledTextInput } from "@/components/FormInputs";
import { VideoPreview } from "@/components/VideoPreview";
import { ImageUploadInput } from "@/components/inputs/ImageInput";
import { PollInput, PollT } from "@/components/inputs/PollInput/PollInput";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { AddPollInput } from "@/gen/graphql";
import { GameMetadata, GamePlyData, Ply, Square } from "@/gen/realtime";
import { useFormFields } from "@/hooks/useForm";
import { GameReport, NestedFeedRecord } from "@/hooks/useRealtimeGame";
import { useViewerContext } from "@/hooks/useViewerContext";
import {
    FeedEntryMode,
    FeedEntryModeSelector,
} from "@/routes/game-feed/components/LiveCommentaryForm";
import {
    addFeedRecordMutation,
    updateFeedRecordMutation,
    useAddPoll,
} from "@/routes/game-feed/query";
import { cn } from "@/utils";
import { getTailwindColor } from "@/utils/colors";
import { reportError } from "@/utils/errors";
import { generateImageUrl } from "@/utils/image";
import { useLanguageCodeWithDefault } from "@/utils/language";

import { ViewChessBoardAnalysis } from "./ChessboardAnalysisInput";
import { SendRecordPushDialogTrigger } from "./FeedRecordNotificationDialog";
import FeedRichTextEditor from "./FeedRichTextEditor";

// Utility functions (extracted outside components for performance)
const parseYoutubeEmbedUrl = (url: string) => {
    const urlParts = url.split("/watch?v=");
    const newUrl =
        urlParts.length == 2
            ? `${url.startsWith("http") ? "" : "https://"}${urlParts[0]}/embed/${urlParts[1]}`
            : undefined;
    return newUrl;
};

const getTwitterId = (url: string) => {
    const urlParts = url.split("/status/");
    return urlParts[1];
};

const getMidpointTimestamp = (date1: Date, date2: Date): string => {
    const time1 = date1.getTime();
    const time2 = date2.getTime();
    const midpointTime = (time1 + time2) / 2;
    return new Date(midpointTime).toISOString();
};

// Memoized component for rich text content rendering
const RichTextContent = React.memo(
    ({ content, highlight }: { content: TElement[]; highlight?: boolean }) => {
        return (
            <>
                {content.map((c, i: number) => (
                    <p
                        key={i}
                        className={cn(
                            `paragraph_${i} `,
                            c.type === "heading"
                                ? cn("font-planar15 text-lg", highlight ? "text-brand-purple" : "")
                                : c.type == "super-heading"
                                  ? "font-planar30 text-xl uppercase"
                                  : "font-planar"
                        )}
                    >
                        {c.children.map((ch, n) =>
                            ch.bold ? (
                                <b key={`bold_el_${n}`}>{(ch.text ?? "") as string}</b>
                            ) : ch.italic ? (
                                <i key={`italic_el_${n}`}>{(ch.text ?? "") as string}</i>
                            ) : (
                                ((ch.text ?? "") as string)
                            )
                        )}
                    </p>
                ))}
            </>
        );
    }
);

export const FeedRecordEntry = React.memo(
    ({
        feedRecord,
        isChild,
        onAddChildRecordClick,
    }: {
        feedRecord: NestedFeedRecord;
        isChild?: boolean;
        onAddChildRecordClick?: () => void;
    }) => {
        const navigate = useNavigate();

        const convertStartingPos = useCallback(
            (
                startingPosition: {
                    color: "b" | "w";
                    square: Square | null;
                    role: "b" | "p" | "n" | "r" | "q" | "k";
                }[]
            ) => {
                const kindC = feedRecord.ply?.kind?.c;

                if (kindC && "from" in kindC && "to" in kindC) {
                    const { from, to } = kindC;
                    const pos: Record<Square, string> = startingPosition.reduce(
                        (a, v) => ({
                            ...a,
                            [v.square != null ? v.square : "a1"]:
                                `${v.color.toLowerCase()}${v.role.toLocaleUpperCase()}`,
                        }),
                        {}
                    );
                    const updatedPos = Object.fromEntries(
                        Object.entries(pos).filter(([key]) => key !== from)
                    );
                    const old = pos[from];
                    updatedPos[to] = old;
                    return updatedPos;
                }
            },
            [feedRecord.ply?.kind?.c]
        );

        return (
            <div className="flex">
                <Card
                    className={cn(
                        "w-full rounded-md border-none p-4 shadow-none",
                        isChild
                            ? "bg-brand-white text-brand-black bg-opacity-60"
                            : feedRecord.bgColor
                              ? getTailwindColor(feedRecord.bgColor)
                              : "bg-feed-dark text-brand-white"
                    )}
                >
                    {feedRecord.draft ? (
                        <div className="flex justify-between">
                            <span className="text-xs italic">Draft</span>
                        </div>
                    ) : null}

                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <span className="text-sm font-semibold">
                                {format(
                                    new Date(feedRecord?.timestamp),
                                    "MM/dd/yyyy '@' kk:mm:ss",
                                    {}
                                )}
                            </span>
                        </div>
                        <Button
                            className={cn(
                                "text-brand-gray-light hover:text-brand-white hover:bg-feed-dark",
                                isChild
                                    ? "bg-brand-white text-brand-black bg-opacity-0 hover:bg-opacity-0"
                                    : "bg-feed-dark text-brand-white"
                            )}
                            onClick={() => navigate(`edit/${feedRecord.id}`)}
                        >
                            <PencilSimple size={16} />
                        </Button>
                    </div>
                    {feedRecord.content ? (
                        <div className="flex flex-row gap-4">
                            {feedRecord.iconImageId ? (
                                <img
                                    src={generateImageUrl({
                                        imageId: feedRecord.iconImageId,
                                        height: 30,
                                        width: 30,
                                    })}
                                    className="object-contain"
                                    alt="Feed record icon URL"
                                />
                            ) : null}

                            <div>
                                <RichTextContent
                                    content={feedRecord.content as TElement[]}
                                    highlight={feedRecord.highlight}
                                />
                            </div>
                        </div>
                    ) : null}
                    {feedRecord.imageId ? (
                        <img
                            src={generateImageUrl({
                                imageId: feedRecord.imageId,
                                height: 100,
                            })}
                            alt="Feed record image URL"
                            className="object-contain"
                        />
                    ) : null}
                    {feedRecord.video ? <VideoPreview video={feedRecord.video} /> : null}
                    {feedRecord.twitterUrl ? (
                        <Tweet id={getTwitterId(feedRecord.twitterUrl)} />
                    ) : null}
                    {feedRecord.youtubeUrl && parseYoutubeEmbedUrl(feedRecord.youtubeUrl) ? (
                        <iframe
                            width="420"
                            height="315"
                            src={parseYoutubeEmbedUrl(feedRecord.youtubeUrl)}
                        ></iframe>
                    ) : null}
                    {feedRecord.poll ? (
                        <div className="my-4 flex space-x-2">
                            <img className="h-6 w-6" src="/Poll.png" />
                            <span>{feedRecord.poll.title}</span>
                        </div>
                    ) : null}

                    {feedRecord.analysis && feedRecord.ply != null ? (
                        <ViewChessBoardAnalysis
                            arrows={feedRecord.analysis.annotatedPlies[0].arrows}
                            highlightedSquares={
                                feedRecord.analysis?.annotatedPlies[0].highlighted_squares
                            }
                            position={convertStartingPos(feedRecord.analysis.startingPosition)}
                            emojiSquares={feedRecord.analysis?.annotatedPlies[0].piece_emojis}
                        />
                    ) : null}

                    <div className="flex w-full justify-between">
                        {feedRecord.ply ? (
                            <span className="text-xs">
                                Linked to half-move {feedRecord.ply.plyIndex + 1} (move{" "}
                                {Math.round((feedRecord.ply.plyIndex + 1) / 2)}.{" "}
                                {feedRecord.ply.san}
                            </span>
                        ) : (
                            <div />
                        )}
                        <div className="ml-2 flex">
                            <SendRecordPushDialogTrigger feedRecord={feedRecord} />

                            {!isChild ? (
                                <a className="cursor-pointer p-2" onClick={onAddChildRecordClick}>
                                    <ArrowBendDownLeft />
                                </a>
                            ) : null}
                        </div>
                    </div>
                </Card>
            </div>
        );
    }
);

export const Feed = ({
    game,
    plyData,
    gameReport,
}: {
    game: GameMetadata;
    plyData: GamePlyData;
    gameReport: GameReport | null;
}) => {
    const sortedFeedRecords = useMemo(
        () => gameReport?.feedRecords ?? [],
        [gameReport?.feedRecords]
    );
    const languageCode = useLanguageCodeWithDefault();
    const { viewer } = useViewerContext();
    const navigate = useNavigate();
    const [{ error: _errorUpdateRecord }, updateFeedRecord] = useMutation(updateFeedRecordMutation);
    const [{ error: _errorAddRecord }, addFeedRecord] = useMutation(addFeedRecordMutation);
    const [removedFeedRecord, setRemovedFeedRecord] = useState<
        { record: NestedFeedRecord; newTimestamp: string } | undefined
    >(undefined);

    const createFeedRecordDraftForPly = useCallback(
        async (ply: Ply) => {
            if (!languageCode) {
                toast.error("Unable to create feed record", {
                    description: "Language code missing",
                    icon: <Languages size={18} />,
                });
                return;
            }

            const result = await addFeedRecord({
                input: {
                    gameId: game.id,
                    languageCode: languageCode as string,
                    draft: true,
                    highlight: false,
                    timestamp: ply.timestamp.toISOString(),
                    commentatorId: viewer?.commentator?.id,
                    plyId: ply.id,
                },
            });
            if (result.error) {
                toast.error("Unable to create feed record for ply", {
                    description: result.error.message,
                });

                return;
            }

            navigate(`edit/${result.data?.addFeedRecord.id}`);
        },
        [addFeedRecord, game.id, languageCode, navigate, viewer?.commentator?.id]
    );

    // Split feedItems computation into separate memoized parts
    const plyItems = useMemo(() => {
        return plyData.plies.map(ply => ({
            type: "ply" as const,
            id: ply.id.toString(),
            timestamp: ply.timestamp,
            content: ply,
            plyIndex: ply.plyIndex,
        }));
    }, [plyData.plies]);

    const feedRecordItems = useMemo(() => {
        return sortedFeedRecords.map(feedRecord => ({
            type: "feedRecord" as const,
            id: feedRecord.id,
            timestamp: feedRecord.timestamp,
            content: feedRecord,
            plyIndex: feedRecord.ply?.plyIndex ?? null,
        }));
    }, [sortedFeedRecords]);

    // Fast lookup set for plies that already have an associated feed record
    const pliesWithFeedRecord = useMemo(() => {
        const s = new Set<string>();
        for (const fr of sortedFeedRecords) {
            if (fr.ply?.id) s.add(fr.ply.id);
        }
        return s;
    }, [sortedFeedRecords]);

    const feedItems = useMemo(() => {
        return [...plyItems, ...feedRecordItems].sort((a, b) => {
            const timestampDiff = b.timestamp.getTime() - a.timestamp.getTime();
            if (timestampDiff !== 0) {
                return timestampDiff;
            }

            // If timestamps are the same, we sort by plyIndex
            const plyIndexA = a.plyIndex !== null && a.plyIndex !== undefined ? a.plyIndex : -1;
            const plyIndexB = b.plyIndex !== null && b.plyIndex !== undefined ? b.plyIndex : -1;

            return plyIndexB - plyIndexA;
        });
    }, [plyItems, feedRecordItems]);

    const getPly = useCallback(
        (plyId: number): JSX.Element | null => {
            const plyItem = feedItems.find(el => el.type === "ply" && el.plyIndex === plyId);

            return plyItem && plyItem.type === "ply" ? (
                <div className="mb-4 flex items-center space-x-2">
                    <span
                        className={cn(
                            "h-5 w-5 rounded-3xl border border-black",
                            plyItem.content.color === "w" ? "bg-white" : "bg-black"
                        )}
                    />
                    <div className="flex flex-col">
                        <span>{`${Math.round((plyItem.content.plyIndex + 1) / 2)}. ${plyItem.content.san}`}</span>
                        <span className="text-brand-black/40 text-xs font-bold">
                            {format(
                                new Date(plyItem.content.timestamp),
                                "dd.MM.yyyy '@' kk:mm:ss.SSS"
                            )}
                        </span>
                    </div>
                </div>
            ) : null;
        },
        [feedItems]
    );

    // Sensors setup
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 10,
            },
        })
    );

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;

            if (over && active.id !== over.id) {
                // Filter visible items (exclude hidden ply elements)
                const visibleItems = feedItems.filter(item => {
                    if (item.type !== "ply") return true;
                    return !feedItems.some(
                        el => el.type == "feedRecord" && el.content.ply?.id == item.id
                    );
                });

                const activeIndex = visibleItems.findIndex(item => item.id === active.id);
                const overIndex = visibleItems.findIndex(item => item.id === over.id);
                const movedItem = feedItems.find(el => el.id == active.id);

                if (activeIndex !== -1 && overIndex !== -1 && movedItem) {
                    const overEl = visibleItems[overIndex];

                    // Determine "underEl" based on the drag direction
                    let underEl;
                    if (activeIndex < overIndex) {
                        underEl =
                            overIndex + 1 < visibleItems.length
                                ? visibleItems[overIndex + 1]
                                : null;
                    } else if (activeIndex > overIndex) {
                        underEl = overIndex - 1 >= 0 ? visibleItems[overIndex - 1] : null;
                    }

                    const newTimestamp = getMidpointTimestamp(
                        underEl ? underEl.timestamp : overEl.timestamp,
                        overEl.timestamp
                    );
                    const removedFeedRecord = movedItem.content as NestedFeedRecord; //Drag is disabled for plys
                    setRemovedFeedRecord({ record: removedFeedRecord, newTimestamp: newTimestamp });
                }
            }
        },
        [feedItems]
    );

    const updateFeedRecordPos = useCallback(async () => {
        if (!removedFeedRecord) {
            return;
        }

        const inputProps = {
            id: removedFeedRecord.record.id,
            timestamp: removedFeedRecord.newTimestamp,
            content: removedFeedRecord.record.content,
            highlight: removedFeedRecord.record.highlight,
            bgColor: removedFeedRecord.record.bgColor,
            textColor: removedFeedRecord.record.textColor,
            twitterUrl:
                removedFeedRecord.record.twitterUrl === ""
                    ? null
                    : removedFeedRecord.record.twitterUrl,
            youtubeUrl:
                removedFeedRecord.record.youtubeUrl === ""
                    ? null
                    : removedFeedRecord.record.youtubeUrl,
            imageId: removedFeedRecord.record.imageId,
            iconImageId: removedFeedRecord.record.iconImageId,
            plyId: removedFeedRecord.record.ply ? removedFeedRecord.record.ply.id : null,
            analysisId: removedFeedRecord.record.analysis
                ? removedFeedRecord.record.analysis.id
                : null,
            pollId: removedFeedRecord.record.poll ? removedFeedRecord.record.poll.id : null,
            commentatorId: viewer?.commentator?.id,
        };

        await updateFeedRecord({
            input: inputProps,
        });
        setRemovedFeedRecord(undefined);
    }, [removedFeedRecord, updateFeedRecord, viewer?.commentator?.id]);

    return (
        <div className="flex w-full flex-col space-y-8 overflow-y-auto">
            <UpdateOrderDialog
                open={removedFeedRecord != undefined}
                onOpenChange={(update: boolean) => {
                    if (update) {
                        updateFeedRecordPos();
                    } else {
                        setRemovedFeedRecord(undefined);
                    }
                }}
            />
            <div className="mx-2">
                <ul className="flex-1 space-y-4">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={feedItems.map(item => item.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            {feedItems.map(item => {
                                switch (item.type) {
                                    case "ply":
                                        return !feedItems.some(
                                            el =>
                                                el.type == "feedRecord" &&
                                                el.content.ply?.id == item.id
                                        ) ? (
                                            <SortableItem
                                                children={
                                                    <PlyItem
                                                        ply={item.content}
                                                        hasFeedRecord={pliesWithFeedRecord.has(
                                                            item.content.id
                                                        )}
                                                        onCreateDraft={() =>
                                                            createFeedRecordDraftForPly(
                                                                item.content
                                                            )
                                                        }
                                                    />
                                                }
                                                key={item.id}
                                                id={item.id}
                                                isDisabled={true}
                                            />
                                        ) : null;

                                    case "feedRecord":
                                        return (
                                            <SortableItem
                                                children={
                                                    <div>
                                                        {item.plyIndex ? (
                                                            <span>
                                                                {item.plyIndex
                                                                    ? getPly(item.plyIndex)
                                                                    : /* Replace with the actual property you want to display */
                                                                      null}
                                                            </span>
                                                        ) : null}
                                                        <FeedRecordItem
                                                            feedRecord={item.content}
                                                            game={game}
                                                        />
                                                    </div>
                                                }
                                                id={item.id}
                                                key={item.id}
                                                isDisabled={item.plyIndex != null}
                                            />
                                        );
                                    default:
                                        return null;
                                }
                            })}
                        </SortableContext>
                    </DndContext>
                </ul>
            </div>
        </div>
    );
};

interface SortableItemProps {
    id: string;
    children: ReactElement;
    isDisabled?: boolean;
}
export const SortableItem: React.FC<SortableItemProps> = ({ id, children, isDisabled }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
        disabled: isDisabled, // Disable sorting when isDisabled is true
    });

    const style = {
        transform: CSS.Transform.toString(
            transform
                ? { ...transform, x: 0, scaleX: 1, scaleY: 1 } // Force scale to remain at 1 during drag
                : null
        ),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <Card
            ref={setNodeRef}
            style={style}
            {...attributes}
            className="flex cursor-default items-center rounded border-b bg-gray-100 p-4"
        >
            {!isDisabled ? (
                <Button
                    className="bg-brand-white mr-2 cursor-move rounded border border-black p-2"
                    {...listeners}
                >
                    ⣿
                </Button>
            ) : null}
            <div className="w-full">{children}</div>
        </Card>
    );
};

const UpdateOrderDialog = ({
    open,
    onOpenChange,
}: {
    open: boolean;
    onOpenChange: (update: boolean) => void;
}) => {
    return (
        <Dialog open={open} onOpenChange={() => onOpenChange(false)}>
            <DialogContent className="max-h-screen overflow-y-auto" aria-describedby={undefined}>
                <DialogHeader>
                    <div className="mr-4 flex items-center justify-between">
                        <DialogTitle>Change order</DialogTitle>
                    </div>
                </DialogHeader>
                <div>
                    <span>Are you sure you want to change the order?</span>
                </div>
                <DialogFooter>
                    <div className="flex space-x-2">
                        <Button onClick={() => onOpenChange(true)}>Yes</Button>
                        <Button variant="secondary" onClick={() => onOpenChange(false)}>
                            No
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const PlyItem = React.memo(
    ({
        ply,
        onCreateDraft,
        hasFeedRecord,
    }: {
        ply: Ply;
        onCreateDraft: () => void;
        hasFeedRecord: boolean;
    }) => {
        return (
            <li className="text-brand-black mt-4 flex items-center justify-between gap-2">
                <div className="flex items-center space-x-2">
                    <span
                        className={cn(
                            "h-5 w-5 rounded-3xl border border-black",
                            ply.color === "w" ? "bg-white" : "bg-black"
                        )}
                    />
                    <div className="flex flex-col">
                        <span>{`${Math.round((ply.plyIndex + 1) / 2)}. ${ply.san}`}</span>
                        <span className="text-brand-black/40 text-xs font-bold">
                            {format(new Date(ply.timestamp), "dd.MM.yyyy '@' kk:mm:ss.SSS")}
                        </span>
                    </div>
                </div>
                {!hasFeedRecord ? (
                    <Button className="bg-brand-black/40" onClick={onCreateDraft}>
                        +
                    </Button>
                ) : null}
            </li>
        );
    },
    (prev, next) => {
        const p = prev.ply;
        const n = next.ply;
        return (
            prev.hasFeedRecord === next.hasFeedRecord &&
            p.id === n.id &&
            p.san === n.san &&
            p.color === n.color &&
            p.plyIndex === n.plyIndex &&
            new Date(p.timestamp).getTime() === new Date(n.timestamp).getTime() &&
            prev.onCreateDraft === next.onCreateDraft
        );
    }
);

const FeedRecordItem = React.memo(
    ({ feedRecord, game }: { feedRecord: NestedFeedRecord; game: GameMetadata }) => {
        const [isChildRecordFormOpen, setChildRecordFormOpen] = useState(false);

        return (
            <div
                className={`flex flex-col ${feedRecord.bgColor ? getTailwindColor(feedRecord.bgColor) : "bg-feed-dark"} space-y-2 rounded p-4`}
                key={`feedRecord-${feedRecord.id}`}
            >
                <FeedRecordEntry
                    feedRecord={feedRecord}
                    onAddChildRecordClick={() => setChildRecordFormOpen(true)}
                />
                {feedRecord.children?.map(c => (
                    <FeedRecordEntry key={`childFeedRecord-${c.id}`} feedRecord={c} isChild />
                ))}
                {isChildRecordFormOpen && (
                    <ChildFeedRecordForm
                        game={game}
                        parentId={feedRecord.id}
                        onDismiss={() => setChildRecordFormOpen(false)}
                    />
                )}
            </div>
        );
    }
);

const ChildFeedRecordForm = ({
    game,
    parentId,
    onDismiss,
}: {
    game: GameMetadata;
    parentId: string;
    onDismiss: () => void;
}) => {
    const { viewer } = useViewerContext();
    const [mode, setMode] = useState<FeedEntryMode>("misc");
    const [{ error: _errorAddRecord }, addFeedRecord] = useMutation(addFeedRecordMutation);
    const languageCode = useLanguageCodeWithDefault();

    const [_addPollError, addPoll] = useAddPoll();

    const form = useFormFields<{
        content: TElement[] | null;
        twitterUrl: string | null;
        youtubeUrl: string | null;
        imageId: string | null;
        poll: PollT | null;
    }>({
        content: null,
        twitterUrl: null,
        youtubeUrl: null,
        imageId: null,
        poll: null,
    });

    const isSubmittable = useMemo(() => {
        return Boolean(
            form.values.content ||
                form.values.twitterUrl ||
                form.values.youtubeUrl ||
                form.values.imageId ||
                form.values.poll
        );
    }, [
        form.values.imageId,
        form.values.poll,
        form.values.content,
        form.values.twitterUrl,
        form.values.youtubeUrl,
    ]);

    const elementRef = useRef<HTMLDivElement>(null!);

    useEffect(() => {
        elementRef?.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
        });
    }, []);

    const onSubmit = useCallback(async () => {
        if (!isSubmittable) {
            return;
        }

        let pollId = null;
        if (form.values.poll) {
            const addPollResult = await addPoll({
                input: form.values.poll,
            });

            if (addPollResult.error) {
                toast.error("Unable to add poll", { description: addPollResult.error.message });
                return;
            }

            pollId = addPollResult.data?.addPoll.id ?? null;
        }

        try {
            const result = await addFeedRecord({
                input: {
                    gameId: game.id,
                    languageCode,
                    content: form.values.content,
                    timestamp: new Date().toISOString(),
                    twitterUrl: form.values.twitterUrl === "" ? null : form.values.twitterUrl,
                    youtubeUrl: form.values.youtubeUrl === "" ? null : form.values.youtubeUrl,
                    imageId: form.values.imageId,
                    commentatorId: viewer?.commentator?.id,
                    draft: false,
                    highlight: false,
                    parentId,
                    pollId,
                },
            });

            if (result.error) {
                toast.error("Error adding record", { description: result.error.message });
                return;
            }
        } catch (e) {
            reportError(e, "Error adding record");
        }
    }, [
        isSubmittable,
        form,
        addPoll,
        addFeedRecord,
        game.id,
        languageCode,
        viewer?.commentator?.id,
        parentId,
    ]);
    return (
        <div ref={elementRef} className="flex w-full flex-col space-x-2 pt-12">
            <div className="flex justify-end space-x-2">
                <button className="text-brand-white align" onClick={() => onDismiss()}>
                    <X size={16} />
                </button>
            </div>
            <div className="flex-1 flex-col space-y-2">
                <FeedEntryModeSelector mode={mode} setMode={setMode} className={"text-white"} />
                {mode === "misc" ? (
                    <div className="flex flex-col gap-4">
                        <div className="bg-brand-white w-full">
                            <FeedRichTextEditor
                                initialValue={form.values.content}
                                onChange={value => form.setValue("content", value)}
                                game={game}
                            />
                        </div>
                        <div className="bg-brand-white flex flex-row p-4">
                            <ImageUploadInput
                                className="w-1/4"
                                imageId={form.values.imageId}
                                onImageId={imageId => form.setValue("imageId", imageId)}
                                label={"Upload image"}
                            />
                        </div>
                    </div>
                ) : mode === "poll" ? (
                    <div className="bg-brand-white">
                        <PollInput
                            poll={
                                form.values.poll
                                    ? form.values.poll
                                    : ({
                                          title: "",
                                          options: [],
                                          endAt: addMinutes(new Date(), 2).toISOString(),
                                      } as AddPollInput)
                            }
                            setPoll={poll => form.setValue("poll", poll)}
                        />
                    </div>
                ) : mode === "twitter" ? (
                    <div className="my-4 flex flex-col gap-4 space-y-2">
                        <ControlledTextInput
                            value={form.values.twitterUrl ?? ""}
                            id="twitter"
                            onValue={twitterUrl => form.setValue("twitterUrl", twitterUrl)}
                            placeholder="Twitter URL"
                            type="url"
                            className="w-full"
                        />
                    </div>
                ) : mode === "youtube" ? (
                    <div className="my-4 flex flex-col gap-4 space-y-2">
                        <ControlledTextInput
                            id="youtube"
                            value={form.values.youtubeUrl ?? ""}
                            onValue={youtubeUrl => form.setValue("youtubeUrl", youtubeUrl)}
                            placeholder="Youtube URL"
                            type="url"
                            className="w-full"
                        />
                    </div>
                ) : null}
                <div className="flex flex-row justify-end">
                    <Button className="w-1/4" onClick={() => onSubmit()}>
                        Submit
                    </Button>
                </div>
            </div>
        </div>
    );
};
